<template>
  <div class="col-auto edit-menu position-static">
    <b-dropdown
      variant="link"
      toggle-class="text-dark text-decoration-none overflow-button initiative-options-button"
      menu-class="overflow-menu"
      class="initiative-options-menu overflow-menu-wrapper"
      :lazy="true"
      boundary="viewport"
      no-caret
    >
      <template slot="button-content">
        <i class="overflow-menu-icon"></i>
      </template>
      <b-dropdown-item @click="$emit('open', $event)">
        <action-key action="Open" :key-combination="['Shift', '<right>']" />
      </b-dropdown-item>
      <b-dropdown-item @click="$emit('edit', $event)">
        <action-key :action="'Edit ' + entityName" :key-combination="['e']" />
      </b-dropdown-item>
      <b-dropdown-item @click="$emit('editNote', $event)"
        >{{ initiative.description ? 'Edit notes' : 'Add notes' }}
      </b-dropdown-item>
      <b-dropdown-divider />
      <b-dropdown-item @click="showAwardModal">
        <div class="d-flex justify-content-between align-items-center">
          <span>Give an award</span>
          <i class="award-icon options-menu-icon"></i>
        </div>
      </b-dropdown-item>
      <b-dropdown-item @click="showMoveInitiative = true">
        <action-key :action="'Move ' + entityName" />
      </b-dropdown-item>
      <b-dropdown-divider />
      <follow-option :initiative-id="initiative.id" />
      <b-dropdown-divider />
      <b-dropdown-item @click="toggleInitiativeClose"
        >{{ initiative.isClosed ? `Re-open ${entityName}` : `Close ${entityName}` }}
      </b-dropdown-item>
      <b-dropdown-item @click="showDeleteModal">Delete {{ entityName }}</b-dropdown-item>
      <template v-if="isAIAllowed">
        <b-dropdown-divider />
        <b-dropdown-item @click="showSummary">Summarize {{ entityName }}</b-dropdown-item>
      </template>
    </b-dropdown>
    <div v-if="showMoveInitiative">
      <move-initiative
        v-model="showMoveInitiative"
        :org-id="initiative.organizationId"
        :initiatives="[initiative]"
        @moved="onInitiativeMove($event)"
      />
    </div>
    <give-award
      v-model="isAwardModalShown"
      :context="awardContext"
      :team-id="teamId"
      @awarded="$emit('awarded')"
      v-if="isAwardModalShown"
    />
    <delete-initiatives-modal
      v-model="isDeleteModalShown"
      v-if="isDeleteModalShown"
      :selected-initiatives="[initiative]"
      @done="$emit('delete-initiatives', $event)"
    />
    <initiative-summary-modal
      :initiative="initiative"
      v-model="isSummaryModalShown"
      v-if="isSummaryModalShown"
    />
  </div>
</template>

<script>
import ActionKey from '@/components/ActionKey'
import GiveAward from '@/components/award/GiveAward'
import FollowOption from '@/components/goal/FollowOption'
import DeleteInitiativesModal from '@/components/initiative/DeleteInitiativesModal.vue'
import MoveInitiative from '@/components/initiative/MoveInitiative'
import { LoadingState } from '@/constants/common'
import { ENTITY_MAP, ENTITY_TYPE } from '@/constants/entities'
import { featureFlags } from '@/constants/featureFlags'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import getEntityAwardContext from '@/utils/getEntityAwardContext'
import { BDropdown, BDropdownDivider, BDropdownItem } from 'bootstrap-vue'
import { mapActions } from 'vuex'

/**
 * This is the component for initiative options dropdown
 * props:
 * - initiative
 *
 * @emits
 * - open
 * - edit
 * - editNote
 * - moved-initiative
 * - delete-initiative
 * - awarded
 */
export default {
  name: 'InitiativeOptions',
  mixins: [FeaturesMixin],
  components: {
    DeleteInitiativesModal,
    ActionKey,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    MoveInitiative,
    GiveAward,
    FollowOption,
    InitiativeSummaryModal: () =>
      import(/* webpackChunkName: "initiative-summary" */ '@/components/initiative/InitiativeSummaryModal.vue')
  },
  props: ['initiative'],
  data() {
    const entityType = this.initiative.parentId ? ENTITY_TYPE.TASK : ENTITY_TYPE.INITIATIVE
    return {
      showMoveInitiative: false,
      entityName: ENTITY_MAP[entityType].name,
      isAwardModalShown: false,
      isSummaryModalShown: false,
      isDeleteModalShown: false,
      summary: null,
      LoadingState
    }
  },
  computed: {
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    owners() {
      return this.initiative.ownerships.map(x => x.userId)
    },
    teamId() {
      return this.initiative.teamId
    },
    awardContext() {
      return {
        ...getEntityAwardContext(this.initiative, 'initiative'),
        initiativeId: this.initiative.id
      }
    },
    isAIAllowed() {
      return this.getFeatureFlagValue(this.initiative.organizationId, featureFlags.ALLOW_AI)
    }
  },
  methods: {
    ...mapActions(['patchInitiative']),
    /**
     * this function is being used by InitiativeRow too
     */
    showDeleteModal() {
      this.isDeleteModalShown = true
    },
    onInitiativeMove(payload) {
      this.$emit('moved-initiative', payload)
      this.showMoveInitiative = false
    },
    async toggleInitiativeClose() {
      let value = !this.initiative.isClosed
      const payload = {
        orgId: this.initiative.organizationId,
        initiativeId: this.initiative.id,
        data: { isClosed: value }
      }
      const initiative = await this.patchInitiative(payload)
      this.$emit('initiative-is-closed-changed', initiative)
    },
    showAwardModal() {
      this.isAwardModalShown = true
    },
    async showSummary() {
      this.isSummaryModalShown = true
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/components/_optionsMenu.scss';

.edit-menu {
  padding: 6px 0 0 8px;
}

/deep/ .initiative-options-button[aria-expanded='false'] {
  display: none;
}

/deep/ .overflow-menu {
  width: 200px;
}

.award-icon {
  background-size: contain;
}

.initiative-summary {
  white-space: pre-wrap;
}
</style>
